import { useState, useRef, useCallback, useEffect, DragEvent, ChangeEvent } from 'react';
import { computeFileChecksum, api } from '../utils/api';
import { formatFileSize } from '../utils/helpers';
import { classNames } from '../utils/helpers';
import type { UploadResult } from '../types';

interface UploadZoneProps {
  onClose: () => void;
  onUpload: (files: File[], displayName?: string) => Promise<UploadResult[]>;
}

const DEFAULT_MAX_FILE_MB = 250;
const DEFAULT_MAX_FILES = 100;
const DEFAULT_MAX_TOTAL_MB = 1000;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'video/mp4', 'video/webm', 'video/quicktime'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.mp4', '.webm', '.mov', '.m4v'];
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime';

// Wichtig: das originale File-Objekt unverändert behalten (kein Spread!),
// sonst geht der Datei-Inhalt verloren und FormData.append schlägt fehl.
interface FileEntry {
  file: File;
  preview?: string;
  checksum?: string;
  error?: string;
}

export function UploadZone({ onClose, onUpload }: UploadZoneProps) {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Limits vom Server (Admin kann sie ändern), mit Fallback falls offline
  const [limits, setLimits] = useState({ maxFileMb: DEFAULT_MAX_FILE_MB, maxFiles: DEFAULT_MAX_FILES, maxTotalMb: DEFAULT_MAX_TOTAL_MB });
  useEffect(() => {
    api.status().then(s => setLimits({
      maxFileMb: s.maxFileSizeMb || DEFAULT_MAX_FILE_MB,
      maxFiles: s.maxFilesPerUpload || DEFAULT_MAX_FILES,
      maxTotalMb: s.maxTotalUploadSizeMb || DEFAULT_MAX_TOTAL_MB,
    })).catch(() => {});
  }, []);

  const validateFile = useCallback((file: File): string | null => {
    const ext = file.name.toLowerCase().split('.').pop();
    const hasValidExt = ext ? ALLOWED_EXTENSIONS.includes(`.${ext}`) : false;
    const hasValidType = ALLOWED_TYPES.includes(file.type);

    if (!hasValidExt || !hasValidType) {
      return `Dateityp nicht erlaubt: ${file.name}`;
    }
    if (file.size > limits.maxFileMb * 1024 * 1024) {
      return `Datei zu groß (max. ${limits.maxFileMb} MB): ${file.name}`;
    }
    return null;
  }, [limits]);

  const addFiles = useCallback(async (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const validEntries: FileEntry[] = [];
    let totalSize = entries.reduce((sum, e) => sum + e.file.size, 0);
    const currentCount = entries.length;
    const errors: string[] = [];

    for (const file of fileArray) {
      if (!(file instanceof File) || file.size === 0) {
        errors.push(`Ungültige Datei übersprungen`);
        continue;
      }
      if (currentCount + validEntries.length >= limits.maxFiles) {
        errors.push(`Maximal ${limits.maxFiles} Dateien erlaubt`);
        break;
      }

      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
        continue;
      }

      if (totalSize + file.size > limits.maxTotalMb * 1024 * 1024) {
        errors.push(`Gesamtgröße würde ${limits.maxTotalMb} MB überschreiten`);
        break;
      }

      totalSize += file.size;

      const entry: FileEntry = { file };
      if (file.type.startsWith('image/') && !file.type.includes('heic') && !file.type.includes('heif')) {
        entry.preview = URL.createObjectURL(file);
      }
      if (file.size <= 100 * 1024 * 1024) {
        try {
          entry.checksum = await computeFileChecksum(file);
        } catch {
          entry.checksum = `${file.name}-${file.size}-${file.lastModified}`;
        }
      } else {
        entry.checksum = `${file.name}-${file.size}-${file.lastModified}`;
      }
      validEntries.push(entry);
    }

    if (errors.length > 0) {
      setError(errors.join('; '));
      setTimeout(() => setError(null), 5000);
    }

    if (validEntries.length > 0) {
      setEntries(prev => [...prev, ...validEntries]);
    }
  }, [entries, validateFile]);

  const removeFile = useCallback((index: number) => {
    setEntries(prev => {
      const entry = prev[index];
      if (entry?.preview) URL.revokeObjectURL(entry.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearFiles = useCallback(() => {
    setEntries(prev => {
      prev.forEach(e => e.preview && URL.revokeObjectURL(e.preview));
      return [];
    });
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    if (e.target) e.target.value = '';
  }, [addFiles]);

  const handleUpload = useCallback(async () => {
    const toUpload = entries.map(e => e.file);
    if (toUpload.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const results = await onUpload(toUpload, displayName || undefined);
      setUploadResults(results);

      const failed = results.filter(r => !r.success).length;

      if (failed === 0) {
        setTimeout(() => {
          clearFiles();
          setDisplayName('');
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setIsUploading(false);
    }
  }, [entries, displayName, onUpload, onClose, clearFiles]);

  const totalSize = entries.reduce((sum, e) => sum + e.file.size, 0);
  const canUpload = entries.length > 0 && !isUploading;

  return (
    <div className="upload-zone-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Fotos hochladen">
      <div className="upload-zone-modal" onClick={e => e.stopPropagation()}>
        <div className="upload-zone-header">
          <h2>Fotos hochladen</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Schließen">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div
          className={classNames('upload-zone-drop-area', 'upload-drop-area', { 'upload-drop-area--active': isDragging })}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            id="file-upload"
            multiple
            accept={ACCEPT}
            onChange={handleFileSelect}
            className="visually-hidden"
            aria-label="Dateien auswählen"
          />
          <label htmlFor="file-upload" className="upload-drop-label">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <p>Dateien hierher ziehen oder <span>auswählen</span></p>
            <p className="upload-drop-hint">
              Fotos (JPEG, PNG, WebP, HEIC) &amp; Videos (MP4, WebM, MOV) &middot; Max. {limits.maxFileMb} MB pro Datei &middot; Max. {limits.maxFiles} Dateien
            </p>
          </label>
        </div>

        {error && (
          <div className="upload-zone-error" role="alert">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        {entries.length > 0 && (
          <div className="upload-zone-files">
            <div className="upload-zone-files-header">
              <h3>{entries.length} Datei{entries.length !== 1 ? 'en' : ''} ausgewählt &middot; {formatFileSize(totalSize)}</h3>
              {entries.length > 1 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={clearFiles}>
                  Alle entfernen
                </button>
              )}
            </div>

            <div className="upload-zone-file-list" role="list">
              {entries.map((entry, index) => (
                <div key={entry.checksum || `${entry.file.name}-${index}`} className="upload-file-item" role="listitem">
                  {entry.preview ? (
                    <img src={entry.preview} alt="" className="upload-file-preview" />
                  ) : (
                    <div className="upload-file-preview upload-file-preview--icon" aria-hidden="true">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                  )}

                  <div className="upload-file-info">
                    <p className="upload-file-name" title={entry.file.name}>{entry.file.name}</p>
                    <p className="upload-file-size">{formatFileSize(entry.file.size)}</p>
                    {entry.error && <p className="upload-file-error">{entry.error}</p>}
                  </div>

                  <button
                    type="button"
                    className="btn btn-ghost btn-icon upload-file-remove"
                    onClick={() => removeFile(index)}
                    aria-label={`Entfernen: ${entry.file.name}`}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <div className="upload-zone-display-name">
              <label htmlFor="display-name" className="label">Dein Name (optional)</label>
              <input
                id="display-name"
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Max Mustermann"
                maxLength={50}
                className="input"
              />
            </div>

            <div className="upload-zone-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isUploading}>
                Abbrechen
              </button>
              <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={!canUpload || isUploading}>
                {isUploading ? (
                  <>
                    <span className="spinner spinner-sm"></span>
                    Hochladen...
                  </>
                ) : (
                  `Upload starten (${entries.length})`
                )}
              </button>
            </div>
          </div>
        )}

        {uploadResults.length > 0 && (
          <div className="upload-zone-results">
            <h3>Ergebnis</h3>
            <p>{uploadResults.filter(r => r.success).length} erfolgreich, {uploadResults.filter(r => !r.success).length} fehlgeschlagen</p>
            {uploadResults.filter(r => !r.success).map((result, i) => (
              <p key={i} className="upload-result-error">
                {result.error}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
